import { PersonalizationService } from '../App/services/personalization-service';
import type { PersonalizedStorage } from '../App/services/personalization-service.contracts';
import type { DashboardFiltersValues } from './dashboard-filters.service';

export interface WidgetPersonalization {
	height?: number;
	width?: number;
	priority?: number;
	hidden?: boolean;
}

export type AutoRefreshInterval = 0 | 30 | 60 | 120 | 240 | 1440; // in minutes
export type AutoSwitchToNextDashboardInterval = number; // in seconds

export interface DashboardPersonalization {
	widgets: Record<string, WidgetPersonalization>;
	autoRefreshInterval?: AutoRefreshInterval;
	filters?: DashboardFilterState;
}

export interface AllDashboardsPersonalization {
	dashboards: Record<string, DashboardPersonalization>;
	hideRefreshPopover: boolean;
	autoSwitchToNextDashboardInterval: AutoSwitchToNextDashboardInterval;
}

export interface DashboardFilterState {
	filters: DashboardFiltersValues;
	timestamp: string;
}

export class DashboardPersonalizationService {
	static readonly $inject = ['mx.SolutionBuilderAgent.PersonalizationService'];

	private personalizedStorage!: PersonalizedStorage<AllDashboardsPersonalization>;
	private personalizedStoragePromise: Promise<void>;
	public autoSwitchToNextDashboardInterval: AutoSwitchToNextDashboardInterval = 0;

	constructor(private readonly personalizationService: ReturnType<typeof PersonalizationService>) {
		this.personalizedStoragePromise = this.personalizationService.getDashboardsStorage().then((storage: PersonalizedStorage<AllDashboardsPersonalization>) => {
			this.personalizedStorage = storage;
		});
	}

	private async getPersonalizationAsync<Key extends keyof DashboardPersonalization>(navigationId: string, key: Key): Promise<DashboardPersonalization[typeof key] | undefined> {
		await this.personalizedStoragePromise;
		return this.personalizedStorage.getValue('dashboards')[navigationId]?.[key];
	}

	private getPersonalization<Key extends keyof DashboardPersonalization>(navigationId: string, key: Key): DashboardPersonalization[typeof key] | undefined {
		return this.personalizedStorage?.getValue('dashboards')[navigationId]?.[key];
	}

	private async savePersonalization<Key extends keyof DashboardPersonalization>(navigationId: string, key: Key, value: DashboardPersonalization[typeof key]) {
		const dashboards = this.personalizedStorage.getValue('dashboards');
		dashboards[navigationId] = dashboards[navigationId] || {};
		dashboards[navigationId][key] = value;
		await this.personalizedStorage.setValue('dashboards', dashboards);
	}

	public async reloadPersonalization() {
		await this.personalizedStorage.reload();
	}

	// Widget personalization methods
	public async getWidgetPersonalizationAsync(navigationId: string): Promise<Record<string, WidgetPersonalization> | undefined> {
		return await this.getPersonalizationAsync(navigationId, 'widgets');
	}

	public getWidgetPersonalization(navigationId: string): Record<string, WidgetPersonalization> | undefined {
		return this.getPersonalization(navigationId, 'widgets');
	}

	public async saveWidgetPersonalization(navigationId: string, widgets: Record<string, WidgetPersonalization>): Promise<void> {
		await this.savePersonalization(navigationId, 'widgets', widgets);
	}

	public hasPersonalization(navigationId: string): boolean {
		const personalizedWidgets = this.getPersonalization(navigationId, 'widgets');
		return Object.keys(personalizedWidgets || {}).length > 0;
	}

	// Filter personalization methods
	public async saveFilterPersonalization(navigationId: string, filters: DashboardFiltersValues): Promise<void> {
		await this.savePersonalization(navigationId, 'filters', {
			filters,
			timestamp: new Date().toISOString(),
		});
	}

	public async getFilterPersonalizationAsync(navigationId: string): Promise<DashboardFiltersValues | undefined> {
		const personalization = await this.getPersonalizationAsync(navigationId, 'filters');
		return personalization?.filters;
	}

	// Auto refresh interval methods
	public async saveAutoRefreshInterval(navigationId: string, autoRefreshInterval: AutoRefreshInterval): Promise<void> {
		await this.savePersonalization(navigationId, 'autoRefreshInterval', autoRefreshInterval);
	}

	public getAutoRefreshInterval(navigationId: string): AutoRefreshInterval {
		const defaultAutoRefreshInterval: AutoRefreshInterval = 120;
		return this.getPersonalization(navigationId, 'autoRefreshInterval') ?? defaultAutoRefreshInterval;
	}

	// Global personalization methods
	public isRefreshPopoverHidden(): boolean {
		return this.personalizedStorage?.getValue('hideRefreshPopover') ?? false;
	}

	public setRefreshPopoverHidden(): Promise<void> {
		return this.personalizedStorage.setValue('hideRefreshPopover', true);
	}

	public getAutoSwitchToNextDashboardInterval(): AutoSwitchToNextDashboardInterval {
		return this.personalizedStorage?.getValue('autoSwitchToNextDashboardInterval') ?? 0;
	}

	public setAutoSwitchToNextDashboardInterval(value: AutoSwitchToNextDashboardInterval): Promise<void> {
		return this.personalizedStorage.setValue('autoSwitchToNextDashboardInterval', value);
	}
}
